import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Star, 
  Loader2, 
  CheckCircle2, 
  XCircle,
  ThumbsUp,
  Sparkles
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FeedbackPage - Customer-facing feedback submission page
 * Accessed via email link: /feedback/{token}
 */
const FeedbackPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const presetRating = searchParams.get('rating');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [rating, setRating] = useState(presetRating ? parseInt(presetRating) : 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(true);

  useEffect(() => {
    fetchFeedbackDetails();
  }, [token]);

  const fetchFeedbackDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/appointment-notifications/feedback/${token}`);
      
      if (response.data.status === 'completed') {
        setSubmitted(true);
      } else {
        setFeedbackData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch feedback details:', err);
      setError(err.response?.data?.detail || 'This feedback link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/appointment-notifications/submit-feedback/${token}`, {
        booking_id: feedbackData.booking_id,
        rating,
        review_text: reviewText || null,
        would_recommend: wouldRecommend
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      alert(err.response?.data?.detail || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Link Expired or Invalid</h1>
            <p className="text-gray-600">{error}</p>
            <p className="text-sm text-gray-500 mt-4">
              If you believe this is an error, please contact support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already submitted state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
            <p className="text-gray-600 mb-4">
              Your feedback has been submitted successfully.
            </p>
            <div className="flex justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-8 w-8 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-500">
              Your review helps other customers find great service providers.
            </p>
            <div className="mt-6 p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">
                <Sparkles className="h-4 w-4 inline mr-1" />
                Thank you for being part of our community!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Feedback form
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8 px-4" data-testid="feedback-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Star className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">How was your experience?</h1>
          <p className="text-gray-600 mt-2">Your feedback helps improve services</p>
        </div>

        {/* Provider Info Card */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
            <p className="text-sm opacity-80">Your appointment with</p>
            <h2 className="text-xl font-bold">{feedbackData?.provider_name}</h2>
            {feedbackData?.service_name && (
              <p className="text-sm opacity-90 mt-1">{feedbackData.service_name}</p>
            )}
          </div>
        </Card>

        {/* Rating Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <Label className="text-base font-medium block text-center mb-4">
              Rate your experience
            </Label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110 focus:outline-none"
                >
                  <Star
                    className={`h-12 w-12 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">
              {rating === 0 && 'Click to rate'}
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent!'}
            </p>
          </CardContent>
        </Card>

        {/* Review Text Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <Label htmlFor="review" className="text-base font-medium block mb-3">
              Tell us more (optional)
            </Label>
            <Textarea
              id="review"
              placeholder="What did you like? What could be improved?"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-gray-500 mt-2">
              Your review will be displayed publicly to help other customers.
            </p>
          </CardContent>
        </Card>

        {/* Recommendation Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ThumbsUp className={`h-6 w-6 ${wouldRecommend ? 'text-green-500' : 'text-gray-400'}`} />
                <div>
                  <Label className="text-base font-medium">Would you recommend?</Label>
                  <p className="text-sm text-gray-500">
                    Would you recommend this provider to friends?
                  </p>
                </div>
              </div>
              <Switch
                checked={wouldRecommend}
                onCheckedChange={setWouldRecommend}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={rating === 0 || submitting}
          className="w-full py-6 text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          {submitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            'Submit Feedback'
          )}
        </Button>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          By submitting, you agree to our review guidelines.
          <br />
          © {new Date().getFullYear()} Zeilian Marketplace
        </p>
      </div>
    </div>
  );
};

export default FeedbackPage;
